import { Injectable } from '@angular/core';
import { CONSTANTS } from '../../Constants/Constants';
import { SellPriceConfig, SellPriceCalculationResult, TaxTypes } from '../../models/sol-estimate-table/SellPriceConfig';
import { EstimateTab, SQWebProject } from '../../models/dtos';
import { CommonDataService } from '../common-data.service';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import {
  EstimateUiModel,
} from '../../models/sol-estimate-table/ui-models/index';
import { roundHalfEven9 } from '../../../types/number-extensions';

const ZERO_SELL_PRICE_RESULT: SellPriceCalculationResult = {
  sellPrice: CONSTANTS.ZERO,
  gmDollars: CONSTANTS.ZERO,
  grossMarginPercentage: CONSTANTS.ZERO,
  markUpPercentage: CONSTANTS.ZERO
};

/**
 * Handles the technology fee row of the estimate tab.
 * Works exclusively with EstimateUiModel — no FormGroup references.
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateTechnologyFeeService {

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService
  ) { }

  // ── Patching (initial load) ────────────────────────────────────

  patchTechnologyFeeCost(
    model: EstimateUiModel,
    estimateValues: EstimateTab,
    config: SellPriceConfig,
    subTotalsCost: number,
    sqWebProject?: SQWebProject
  ): void {
    // SAP Submittal: In read-only mode, use pre-calculated values from estimateTab directly
    if (config.isEstimateReadOnly) {
      this.applyReadOnlyTechnologyFee(model, estimateValues, subTotalsCost);
      return;
    }

    const techFeeCost = sqWebProject.technologyFeeCategory.sumUpCost;

    // For Line Item mode (null) and Fixed Margin mode (false), derive cost from sell prices.
    if (config.isFixedPriceSelected === null || config.isFixedPriceSelected === false) {
      this.recalculateTechnologyFee(model, config, subTotalsCost);
      return;
    }
    // Fixed Price mode — use the value as-is.
    this.estimateCommonFunctions.updateStandaloneRowWithSellPrice(model.technologyFee, techFeeCost, config, subTotalsCost);
  }

  // ── Recalculation ──────────────────────────────────────────────

  recalculateTechnologyFeeRow(model: EstimateUiModel, config: SellPriceConfig): void {
    // SAP Submittal: In read-only mode, skip recalculation
    if (config.isEstimateReadOnly) {
      return;
    }

    const stc = model.subTotals.cost;

    if (config.isFixedPriceSelected === null || config.isFixedPriceSelected === false) {
      this.recalculateTechnologyFee(model, config, stc);
    } else {
      // Fixed Price mode — cost = sellAsSellPrice × techFeePercent
      this.recalculateFixedPriceTechnologyFee(model, config, stc);
    }
  }

  // ── Technology Fee Calculation ─────────────────────────────────

  recalculateTechnologyFee(model: EstimateUiModel, config: SellPriceConfig, subTotalsCost?: number): void {
    // SAP Submittal: In read-only mode, skip recalculation
    if (config.isEstimateReadOnly) {
      return;
    }

    const stc = subTotalsCost ?? model.subTotals.cost;
    const techFeePercent = config.techFeePercent;
    const gmPercent = config.technologyFeeGMPercent;

    const sumOfSellPrices = this.collectSumOfSellPrices(model, config);
    const { cost, result } = this.calculateTechnologyFeeCost(
      sumOfSellPrices, techFeePercent, gmPercent, config.isMarkUpSelected
    );

    model.technologyFee.cost = cost;
    this.estimateCommonFunctions.updateRowResult(model.technologyFee, result, stc);
  }

  // ── Reactive Handler ───────────────────────────────────────────

  onTechnologyFeePercentChanged(
    model: EstimateUiModel,
    config: SellPriceConfig,
    newPercent: number,
    isGMPercent: boolean
  ): { updatedConfig: SellPriceConfig } {
    // User-entered tech fee percent is clamped to 0–99%
    const clampedPercent = Math.max(CONSTANTS.ZERO, Math.min(99, newPercent));

    if (config.isFixedPriceSelected === null || config.isFixedPriceSelected === false) {
      const modePercent = this.toModePercent(clampedPercent, isGMPercent, config.isMarkUpSelected);
      const updatedConfig = { ...config, technologyFeeGMPercent: modePercent };
      this.recalculateTechnologyFee(model, updatedConfig);
      return { updatedConfig };
    }

    // Fixed Price mode — standard calculation.
    const isMarkUp = !isGMPercent;
    const row = model.technologyFee;
    const cost = row.cost;
    const stc = model.subTotals.cost;

    const result = this.estimateCommonFunctions.calculateRowSellPrice(cost, clampedPercent, isMarkUp);
    this.estimateCommonFunctions.updateRowResult(row, result, stc);

    const updatedPercent = this.toModePercent(clampedPercent, isGMPercent, config.isMarkUpSelected, result);
    const updatedConfig = { ...config, technologyFeeGMPercent: updatedPercent };
    return { updatedConfig };
  }

  /** Expresses the entered percent in the active mode's units (GM% or MU%). */
  private toModePercent(
    entered: number,
    isGMPercent: boolean,
    isMarkUpSelected: boolean,
    fixedPriceResult?: SellPriceCalculationResult
  ): number {
    if (isMarkUpSelected !== isGMPercent) {
      return entered;
    }
    if (fixedPriceResult) {
      return isMarkUpSelected ? fixedPriceResult.markUpPercentage : fixedPriceResult.grossMarginPercentage;
    }
    return isMarkUpSelected
      ? this.estimateCommonFunctions.getMarkUpPercent(entered)
      : this.estimateCommonFunctions.getGrossMarginPercent(entered);
  }

  // ── Refresh Displayed Percentages (GM↔MU toggle) ──────────────

  refreshTechnologyFeePercentages(model: EstimateUiModel): void {
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(model.technologyFee);
  }

  // ── SAP Submittal: Read-Only Mode ──────────────────────────────

  /**
   * In read-only (SAP submittal) mode, applies the pre-calculated technology fee
   * cost and sell price from EstimateTab directly, deriving GM%/MU% from them.
   */
  private applyReadOnlyTechnologyFee(
    model: EstimateUiModel,
    estimateValues: EstimateTab,
    subTotalsCost: number
  ): void {
    const cost = estimateValues.technologyFeeCost ?? CONSTANTS.ZERO;
    const sellPrice = estimateValues.technologyFeeSellprice ?? CONSTANTS.ZERO;

    model.technologyFee.cost = cost;

    const result = this.estimateCommonFunctions.recomputePercentsFromSellPrice(cost, sellPrice);
    this.estimateCommonFunctions.updateRowResult(model.technologyFee, result, subTotalsCost);
  }

  // ── Sum of Sell Prices Collection ──────────────────────────────

  private collectSumOfSellPrices(model: EstimateUiModel, config: SellPriceConfig): number {

    // Material total sell price (from materialCostsHeader)
    const materialSP = model.materialCosts.materialCostsHeader.sellPrice;

    // Labor total sell price (from laborCostsHeader)
    const laborSP = model.laborCosts.laborCostsHeader.sellPrice;

    // Subcontractor total sell price
    const subContractorSP = model.subContractorCosts.headerRow.sellPrice;

    // Named misc/risk/travel sell prices
    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;

    const namedSP = (name: string): number => {
      const row = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, name);
      return row ? row.sellPrice : CONSTANTS.ZERO;
    };

    const miscCostSP = namedSP('MISCELLANEOUS_COSTS');
    const travelSP = namedSP('TRAVEL_EXPENSE_COSTS');
    const riskSP = namedSP('PROJECT_ENTERED_RISK_COSTS');
    const productivityReserveSP = namedSP('PRODUCTIVITY_RESERVE');
    const subcontractorFeeSP = namedSP('SUBCONTRACTOR_FEE');
    // Mechanical fee should be zero for estimates other than mechanical
    const mechanicalFeeSP = namedSP('MECHANICAL_FEE');

    // Auto-risk sell price (only if useAutoRisk is enabled)
    const useAutoRisk = this.commonDataService.getEstimateMetaData()?.estimateSettings?.useAutoRisk ?? false;
    const autoRiskSP = useAutoRisk ? namedSP('CALCULATED_RISK_COSTS') : CONSTANTS.ZERO;

    // Tax (UseTax) sell price
    const taxSP = this.getTaxSellPrice(model, config.taxTypeID);
    // Must cover every row that feeds Sell-As, or Fixed Margin and Fixed Price tech fees diverge.
    return materialSP + laborSP + subContractorSP
      + miscCostSP + travelSP + riskSP + autoRiskSP
      + subcontractorFeeSP + mechanicalFeeSP
      + taxSP + productivityReserveSP;
  }

  private getTaxSellPrice(model: EstimateUiModel, taxTypeID: number): number {
    if (taxTypeID === TaxTypes.UseTax || taxTypeID === TaxTypes.HST || taxTypeID === TaxTypes.PST) {
      return model.taxMaterialRate.headerRow.sellPrice;
    }
    if (taxTypeID === TaxTypes.NoTax) {
      return model.taxNotIncluded.sellPrice;
    }
    return model.taxMaterialLaborRate.headerRow.sellPrice;
  }

  // ── Technology Fee Cost Formula ─────────────────────────────────

  private calculateTechnologyFeeCost(
    sumOfSellPrices: number,
    techFeePercent: number,
    gmPercent: number,
    isMarkUpSelected: boolean
  ): { cost: number; result: SellPriceCalculationResult } {
    // Formula requires true GM%; MU% legitimately exceeds 100, so convert before the guard.
    const trueGMPercent = isMarkUpSelected ? this.estimateCommonFunctions.getGrossMarginPercent(gmPercent) : gmPercent;

    if (techFeePercent === CONSTANTS.ZERO || trueGMPercent >= CONSTANTS.HUNDRED) {
      return {
        cost: CONSTANTS.ZERO,
        result: this.estimateCommonFunctions.calculateRowSellPrice(CONSTANTS.ZERO, gmPercent, isMarkUpSelected)
      };
    }

    const techFeeDecimal = techFeePercent / CONSTANTS.HUNDRED;
    const gmDecimal = trueGMPercent / CONSTANTS.HUNDRED;

    // Business rule: a ratio >= 1 is treated as 0.99
    const divisor = Math.min(techFeeDecimal / (1 - gmDecimal), 0.99);
    const denominator = 1 - divisor;

    const cost = roundHalfEven9((sumOfSellPrices / denominator) * techFeeDecimal);
    const result = this.estimateCommonFunctions.calculateRowSellPrice(cost, gmPercent, isMarkUpSelected);

    return { cost, result };
  }

  // ── Fixed Price Technology Fee ──────────────────────────────────

  /**
   * In Fixed Price mode the technology fee cost is simply:
   *   cost = sellAsSellPrice × techFeePercent / 100
   *
   * Sell price and GM are then derived from that cost using the
   * sold-as GM% (or MU%) that was already derived for Fixed Price.
   */
  private recalculateFixedPriceTechnologyFee(
    model: EstimateUiModel,
    config: SellPriceConfig,
    subTotalsCost: number
  ): void {
    const sellAsSP = model.sellAs.sellAsSellPrice;
    const techFeePercent = config.techFeePercent;

    if (techFeePercent === CONSTANTS.ZERO) {
      model.technologyFee.cost = CONSTANTS.ZERO;
      const zeroResult = this.estimateCommonFunctions.calculateRowSellPrice(
        CONSTANTS.ZERO, config.technologyFeeGMPercent, config.isMarkUpSelected
      );
      this.estimateCommonFunctions.updateRowResult(model.technologyFee, zeroResult, subTotalsCost);
      return;
    }


    // The previous tech fee is inside subTotalsCost, so exclude it or a zero-cost estimate never reads as zero.
    const costWithoutTechFee = subTotalsCost - model.technologyFee.cost;
    const cost = costWithoutTechFee === CONSTANTS.ZERO ? CONSTANTS.ZERO : roundHalfEven9(sellAsSP * (techFeePercent / CONSTANTS.HUNDRED));
    model.technologyFee.cost = cost;

    // Formula requires true GM%; convert from MU% if in markup mode
    const trueGMPercent = config.isMarkUpSelected
      ? this.estimateCommonFunctions.getGrossMarginPercent(config.technologyFeeGMPercent)
      : config.technologyFeeGMPercent;
    const techFeeDecimal = trueGMPercent / CONSTANTS.HUNDRED;
    const divisor = (1 - techFeeDecimal);

    const sellPrice = divisor === CONSTANTS.ZERO ? CONSTANTS.ZERO : roundHalfEven9(cost / divisor);
    const result = cost === CONSTANTS.ZERO
      ? {
        ...ZERO_SELL_PRICE_RESULT,
        ...this.estimateCommonFunctions.resolvePercentPair(config.technologyFeeGMPercent, config.isMarkUpSelected)
      }
      : this.estimateCommonFunctions.recomputePercentsFromSellPrice(cost, sellPrice);
    this.estimateCommonFunctions.updateRowResult(model.technologyFee, result, subTotalsCost);
  }
}
